import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Typography,
  Box,
} from '@mui/material';
import { Download as DownloadIcon, Close } from '@mui/icons-material';
import { authedFetch, useSnackbarStore } from '@certara/common-react-components';

interface ReportDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  downloadUrl: string;
  showRelativePaths?: boolean;
}

export function ReportDialog({
  open,
  onClose,
  title,
  downloadUrl,
  showRelativePaths = false,
}: ReportDialogProps) {
  const [format, setFormat] = useState<number>(0);
  const [useRelativePaths, setUseRelativePaths] = useState(false);
  const { addSnackbar } = useSnackbarStore();

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormat(0);
      setUseRelativePaths(false);
    }
  }, [open]);

  const handleDownload = useCallback(async () => {
    try {
      const separator = downloadUrl.includes('?') ? '&' : '?';
      let fullUrl = `${downloadUrl}${separator}format=${format}`;
      if (showRelativePaths) {
        fullUrl += `&useRelativePaths=${useRelativePaths}`;
      }

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const finalUrl = apiBaseUrl ? `${apiBaseUrl}/${fullUrl}` : fullUrl;

      // authedFetch attaches the bearer (so it works in CAD mode, where
      // cookie auth silently fails — GLOB-1016) and handles 401 → refresh →
      // retry → visible sign-in. We keep the raw Response to read
      // Content-Disposition below.
      const response = await authedFetch(finalUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      const filenameMatch = disposition?.match(/filename[^;=\n]*=(['"]?)([^'";\n]*)\1/);
      const filename = filenameMatch?.[2]?.trim() ?? 'report';

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 100);

      addSnackbar('Download will start momentarily', 'success');
      onClose();
    } catch (error) {
      addSnackbar(
        'Report download failed',
        'error',
        error instanceof Error ? error.message : String(error),
        true
      );
    }
  }, [downloadUrl, format, useRelativePaths, showRelativePaths, addSnackbar, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid="report-dialog"
    >
      <DialogTitle data-testid="report-dialog-title">
        <Typography variant="h6">{title}</Typography>
      </DialogTitle>

      <DialogContent data-testid="report-dialog-content">
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <FormControl>
            <FormLabel id="report-format-label">Format</FormLabel>
            <RadioGroup
              aria-labelledby="report-format-label"
              value={format}
              onChange={(e) => setFormat(Number(e.target.value))}
              data-testid="report-dialog-format-group"
            >
              <FormControlLabel
                value={0}
                control={<Radio data-testid="report-dialog-format-rtf" />}
                label="RTF"
              />
              <FormControlLabel
                value={1}
                control={<Radio data-testid="report-dialog-format-csv" />}
                label="CSV"
              />
            </RadioGroup>
          </FormControl>

          {showRelativePaths && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={useRelativePaths}
                  onChange={(e) => setUseRelativePaths(e.target.checked)}
                  data-testid="report-dialog-relative-paths-checkbox"
                />
              }
              label="Use relative paths"
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          startIcon={<Close />}
          data-testid="report-dialog-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={<DownloadIcon />}
          data-testid="report-dialog-download-button"
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}
